import React, { useMemo } from 'react'

import CheckBox from './CheckBox'
import InfoPopover from './InfoPopover'

// Disclosure of which fields actually flow to whom when the donor
// consents — partner gets name + email, the organization gets the
// same plus whichever of phone / address the partner is collecting.
// Mirrors v6's tooltip body but as JSX (instead of a templated HTML
// string) so we don't have to dangerouslySetInnerHTML it. Only
// renders when at least one of phone / address is being collected;
// otherwise the popover would just say "we share name and email"
// twice with no extra information for the donor.
const SharedFields = ({ i18n, optionKey, organizer, collect_phone, collect_address }) => {
  const includesPartner = optionKey.includes('partner')
  const partnerWhom = i18n.t('personal_data_sharing.partner', { partner: organizer })
  const orgWhom = i18n.t('personal_data_sharing.organization')
  return (
    <>
      {includesPartner && (
        <>
          <p className="mb-1">
            {i18n.t('personal_data_sharing.shared_with', { whom: partnerWhom })}
          </p>
          <ul className="mb-2 ps-4 list-disc">
            <li>{i18n.t('name')}</li>
            <li>{i18n.t('email')}</li>
          </ul>
        </>
      )}
      <p className="mb-1">
        {i18n.t('personal_data_sharing.shared_with', { whom: orgWhom })}
      </p>
      <ul className="ps-4 list-disc">
        <li>{i18n.t('name')}</li>
        <li>{i18n.t('email')}</li>
        {collect_phone && <li>{i18n.t('phone')}</li>}
        {collect_address && <li>{i18n.t('address')}</li>}
      </ul>
    </>
  )
}

const DataSharing = ({
  i18n, foundation, partner_is_pledge, data_sharing, collect_address, collect_phone,
  beneficiary, partner, defaultShares, shares, setShares,
}) => {
  const singleKey = useMemo(() => {
    if (!foundation && beneficiary.type !== 'Crowdfund')
      return 'partner'
    if (partner.organization_id === beneficiary.id || partner_is_pledge)
      return 'organization'
  }, [ foundation, partner_is_pledge, beneficiary, partner ])

  const key = useMemo(() =>
    singleKey || `partner_and_${beneficiary.type.toLowerCase()}`
  , [ singleKey, beneficiary ])

  const organizer = useMemo(() =>
    partner.business ? partner.name : i18n.t('personal_data_sharing.organizer')
  , [ partner ])

  const options = useMemo(() => {
    const whom = i18n.t(`personal_data_sharing.${key}`, { partner: organizer })
    const extended = collect_address || collect_phone ? '_extended' : ''

    switch (data_sharing.display) {
    case 'split_fundraiser':
      return [
        {
          key,
          info: defaultShares.filter(s => s.includes('partner') || s.includes('beneficiary')),
          labelText: i18n.t(`personal_data_sharing.${data_sharing.behavior}${extended}`, { whom }),
          extended,
        },
        {
          key: 'public',
          info: defaultShares.filter(s => s.includes('public')),
          labelText: i18n.t(`personal_data_sharing.${data_sharing.behavior}_public`),
        },
      ]
    case 'split':
    case 'split_with_public':
      if (!singleKey) {
        const checkboxes = [
          {
            key: 'partner',
            whom: i18n.t('personal_data_sharing.partner', { partner: organizer }),
            extended: '',
          },
          {
            key: 'beneficiary',
            whom: i18n.t(`personal_data_sharing.${beneficiary.type.toLowerCase()}`),
            extended,
          },
        ].map(({ key, whom, extended }) => {
          const info = defaultShares.filter(s => s.includes(key))
          const labelText =
            i18n.t(`personal_data_sharing.${data_sharing.behavior}_split.${key}`, { whom })
            || i18n.t(`personal_data_sharing.${data_sharing.behavior}${extended}`, { whom })
          return { key, info, labelText, extended }
        })
        if (data_sharing.display === 'split_with_public') {
          checkboxes.push({
            key: 'public',
            info: defaultShares.filter(s => s.includes('public')),
            labelText: i18n.t(`personal_data_sharing.${data_sharing.behavior}_public`),
          })
        }
        return checkboxes
      }
    case 'single_fundraiser':
    case 'single':
      const suffix = extended
        || ([ 'single_fundraiser', 'split_with_public' ].includes(data_sharing.display) ? '_with_public' : '')
      return [
        {
          key,
          labelText: i18n.t(`personal_data_sharing.${data_sharing.behavior}${suffix}`, { whom }),
          info: defaultShares,
          extended,
        },
      ]
    }
  }, [ data_sharing, collect_address, collect_phone, beneficiary, defaultShares ])

  return options?.map(({ key, info, labelText, extended }) => {
    const sharing = shares.includes(info[0])
    return (
      <CheckBox
        key={key}
        id={`personal-data-sharing-${key}`}
        checked={data_sharing.behavior === 'opt_out' ? !sharing : !!sharing}
        onChange={() => setShares(shares =>
          sharing ? shares.filter(s => !info.includes(s)) : shares.concat(info)
        )}
        label={<span dangerouslySetInnerHTML={{ __html: labelText }} />}
        afterLabel={extended && (
          <InfoPopover label={i18n.t('learn_more')} boundary=".widget-content">
            <SharedFields
              i18n={i18n}
              optionKey={key}
              organizer={organizer}
              collect_phone={collect_phone}
              collect_address={collect_address}
            />
          </InfoPopover>
        )}
      />
    )
  })
}

export default DataSharing
